import {
  FileSignature,
  FileStack,
  FileText,
  LayoutDashboard,
  Menu,
  Settings,
  Sparkles,
  Users,
} from "lucide-react";
import { useState, type ComponentType } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, useLocation } from "react-router";
import { LanguageSwitcher } from "./language-switcher";
import { InstallApp } from "@/components/pwa/pwa";
import { HelpButton } from "./help-dialog";
import { ThemeToggle } from "./theme-toggle";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { env } from "@/lib/core/env";
import { cn } from "@/lib/core/utils";

type Item = { to: string; label: string; icon: ComponentType<{ className?: string }> };

/**
 * Bottom tab bar on phones (the header nav is hidden below md): dashboard, invoices, quotes and
 * the assistant, and "More" for customers, templates, settings, language and theme. Sits above the home indicator (safe area).
 */
export function MobileNav() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [more, setMore] = useState(false);

  const tabs: Item[] = [
    { to: "/dashboard", label: t("nav.dashboard"), icon: LayoutDashboard },
    { to: "/invoices", label: t("nav.invoices"), icon: FileText },
    { to: "/quotes", label: t("nav.quotes"), icon: FileSignature },
    { to: "/assistant", label: t("nav.assistant"), icon: Sparkles },
  ];
  const extra: Item[] = [
    { to: "/customers", label: t("nav.customers"), icon: Users },
    { to: "/templates", label: t("nav.templates"), icon: FileStack },
    { to: "/settings", label: t("nav.settings"), icon: Settings },
  ];
  const inMore = extra.some((item) => pathname.startsWith(item.to));

  const tabClass = (active: boolean) =>
    cn(
      "flex flex-col items-center justify-center gap-0.5 py-1.5 text-[11px] font-medium transition-colors",
      active ? "text-foreground" : "text-muted-foreground",
    );

  return (
    <>
      <nav
        aria-label={t("nav.main")}
        className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-5 border-t bg-background/95 pr-[env(safe-area-inset-right)] pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)] backdrop-blur md:hidden"
        data-testid="mobile-nav"
      >
        {tabs.map(({ to, label, icon: Icon }) => (
          <NavLink key={to} to={to} className={({ isActive }) => tabClass(isActive)}>
            <Icon className="size-5" />
            <span className="max-w-full truncate px-1">{label}</span>
          </NavLink>
        ))}
        <button
          type="button"
          className={tabClass(inMore || more)}
          onClick={() => setMore(true)}
          aria-haspopup="dialog"
          data-testid="mobile-more"
        >
          <Menu className="size-5" />
          <span>{t("nav.more")}</span>
        </button>
      </nav>

      <Sheet open={more} onOpenChange={setMore}>
        <SheetContent
          side="bottom"
          className="rounded-t-2xl pb-[max(1.5rem,env(safe-area-inset-bottom))]"
        >
          <SheetTitle className="sr-only">{t("nav.more")}</SheetTitle>
          <div className="flex flex-col gap-1 pt-2">
            {extra.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                onClick={() => setMore(false)}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 rounded-lg px-3 py-3 text-base",
                    isActive ? "bg-accent" : "hover:bg-accent",
                  )
                }
              >
                <Icon className="size-5 text-muted-foreground" /> {label}
              </NavLink>
            ))}
          </div>
          <HelpButton variant="row" />
          <div className="mt-2 flex items-center gap-2 border-t pt-4">
            <LanguageSwitcher />
            <ThemeToggle />
          </div>
          <div className="mt-3 empty:hidden">
            <InstallApp />
          </div>
          <p className="mt-4 text-center text-xs text-muted-foreground" data-testid="app-version">
            {env.appName} v{env.appVersion}
          </p>
        </SheetContent>
      </Sheet>
    </>
  );
}
